import './TcoActionPanel.scss';

import { AdjustmentsService, MetadataService, PortfolioService, PricingService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import {
    AssessmentConfiguration, AssessmentMetadata, DownloadAssessmentParams, DownloadAssessmentResponse, TcoAssessment, TcoAssessmentSummaryOutput
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, ButtonDropdown, ButtonDropdownProps, Container, NonCancelableCustomEvent, SpaceBetween,
} from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useLocation, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { FormAction } from '../../common/form-builder/types';
import { constants } from '../../common/Localization';
import { awsRightSizePolicyIdToString } from '../../common/RightSizingPolicyName';
import { ErrorWithResponse, MatchParam } from '../../common/types';
import { PortfolioContext } from '../../contexts/PortfolioContext';
import { RecentPortfoliosContext } from '../../contexts/RecentPortfoliosContext';
import { TcoContext } from '../../contexts/TcoContext';
import { customizeErrorMessage } from '../../utils/errorMessageCustomizer';
import ConfirmationModal from '../common/ConfirmationModal';
import AssessmentControlsModal, { CreateNewAssessment, EditAssessment } from './assessment-controls/AssessmentControlsModal';
import SavingsSummary from './SavingsSummary';
import { getTcoSubPage } from './TcoActionPanel-helper';
import TcoAssumptionsModal from './TcoAssumptionsModal';
import {
    addDefaultConfigurations,
    createTCOAssessment,
    defaultTcoAssessmentId,
    deleteAssessmentFromPortfolio,
    generateNewTcoAssessment,
    MAX_ASSESSMENTS_PER_PORTFOLIO,
    updateAssessmentName,
    updatePortfolioWithTcoAssessment,
    updateTCOAssessment,
} from './TCOHelper';

const tcoService = new TcoService();
const portfolioService = new PortfolioService();
const metadataService = new MetadataService();
const pricingService = new PricingService();
const adjustmentsService = new AdjustmentsService();

type Props = FlashContextInterface & {
    visible: boolean;
    hideSavingsSummary: boolean;
};

function TcoActionPanel({ flash, visible, hideSavingsSummary }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const history = useHistory();
    const location = useLocation();
    const portfolioId = match?.params?.portfolioId ?? '';
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? '';
    const isMounted = useIsMounted();

    const [ tcoExists, setTcoExists ] = useState(true);
    const [ tcoAssumptionModalVisible, setTcoAssumptionModalVisible ] = useState(false);
    const [ assessmentModalVisible, setAssessmentModalVisible ] = useState(false);
    const [ assessmentDeleteModalVisible, setAssessmentDeleteModalVisible ] = useState(false);
    const [ assessmentModalAction, setAssessmentModalAction ] = useState<FormAction>(FormAction.ADD);
    const [ assessmentToEdit, setAssessmentToEdit ] = useState<EditAssessment>();
    const [ tcoSummary, setTcoSummary ] = useState<TcoAssessmentSummaryOutput | null>(null);
    const [ downloadLoading, setDownloadLoading ] = useState(false);
    const [ outOfDatePricing, setOutOfDatePricing ] = useState(false);

    const { portfolio, updatePortfolio } = useContext(PortfolioContext);
    const { refreshList } = useContext(RecentPortfoliosContext);

    const {
        tcoLoading, assessment, assessmentToUpdate, assessmentMetadatas, setLocalAssessment, updateAssessment, updateAssessmentDescription, updateTcoLoading,
        updateAssessmentMetadatas,
    } = useContext(TcoContext);

    const assessmentActions: ButtonDropdownProps.Items = [
        {
            id: 'create',
            text: constants.CREATE_NEW_ASSESSMENT,
            disabled: assessmentMetadatas.length === 0 || assessmentMetadatas.length >= MAX_ASSESSMENTS_PER_PORTFOLIO,
        },
        {
            id: 'edit',
            text: constants.EDIT_ASSESSMENT,
            disabled: assessmentMetadatas.length === 0,
        },
        {
            id: 'compare',
            text: constants.COMPARE_ASSESSMENTS,
            disabled: assessmentMetadatas.length === 0,
        },
        {
            id: 'delete',
            text: constants.DELETE_ASSESSMENT,
            disabled: assessmentMetadatas.length <= 1,
        }
    ];

    const redirectToNewAssessment = (assessmentId: string) => {
        const currentPage = getTcoSubPage(location.pathname);
        history.push(`/portfolio/${portfolioId}/tco/${assessmentId}${currentPage}`);
    };

    const redirectToDefaultAssessment = async () => {
        try {
            updateTcoLoading(true);
            const { defaultTcoAssessmentId } = portfolio;
            if (defaultTcoAssessmentId) {
                setTcoExists(true);
                history.replace(location.pathname.replace('default', defaultTcoAssessmentId));
            }
            else {
                setTcoExists(false);
                await loadMetadata();
                await loadData();
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_REDIRECTING);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
            return '';
        }
    };
    
    const loadAssumptionData = async (region = '') => {
        const description = await tcoService.getDescription(region);
        updateAssessmentDescription(description.configuration.properties);
    };

    const loadData = async () => {
        if (tcoAssessmentId !== 'default') {
            updateTcoLoading(true);
            setTcoExists(true);
            try {
                const tcoSummary = await tcoService.getSummary(tcoAssessmentId);
                const tcoAssessment: TcoAssessment = {
                    assumptionName: tcoSummary.assumptionName,
                    configuration: addDefaultConfigurations(tcoSummary.configuration),
                    createdAt: tcoSummary.createdAt,
                    createdBy: tcoSummary.createdBy,
                    id: tcoSummary.id,
                    input: tcoSummary.input,
                    updatedAt: tcoSummary.updatedAt,
                    updatedBy: tcoSummary.updatedBy,
                };
                updateAssessment(tcoAssessment);
                setTcoSummary(tcoSummary.output);
                await loadAssumptionData(tcoSummary.configuration.tcoAssumptions.awsRegion);
            } catch (err) {
                if (isMounted()) {
                    flash.alert(FlashType.error, (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message,
                        constants.ERROR_LOADING_ASSESSMENT_DATA);
                }
            } finally {
                if (isMounted()) {
                    updateTcoLoading(false);
                }
            }
            // if TCO does not exist, create new TCO Assessment and allow use to interact with assumptions
        } else {
            const tcoAssessment: TcoAssessment = generateNewTcoAssessment(portfolioId, portfolio?.name ?? '');
            setTcoExists(false);
            setLocalAssessment(tcoAssessment);
            await loadAssumptionData();
        }
    };

    const checkOutofDatePricing = async () => {
        // Only need to check assessment, not assessmentToUpdate, since service call will trigger an assessment update
        if (assessment) {
            const pricingUpdatedTime = await pricingService.getPriceLastUpdatedTime();
            const isPricingOutdated = pricingUpdatedTime > assessment.updatedAt;
            if (isPricingOutdated !== outOfDatePricing) {
                setOutOfDatePricing(isPricingOutdated);
            }
        }
    };

    useEffect(() => {
        checkOutofDatePricing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    useEffect(() => {
        if (tcoAssessmentId === 'default') {
            redirectToDefaultAssessment();
        } else {
            setTcoExists(true);
            loadMetadata();
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ tcoAssessmentId ]);

    const loadMetadata = async () => {
        const { tcoAssessmentIds } = portfolio;
        if (tcoAssessmentIds) {
            const assessmentMetadatas: AssessmentMetadata[]  = await metadataService.getList(tcoAssessmentIds);
            updateAssessmentMetadatas(assessmentMetadatas);
        }
    };

    useEffect(() => {
        if (tcoAssessmentId !== 'default') {
            loadMetadata();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolio ]);

    /**
     * Saves the new Assessment to the current portfolio
     * @param newAssessment 
     */
    const saveNewAssessment = async (newAssessment : TcoAssessment) : Promise<void> => {
        await updateAssessmentName(newAssessment.id, 'Assessment 1', metadataService);
        const updatedPortfolio = await updatePortfolioWithTcoAssessment(portfolio, newAssessment.id, portfolioService);
        updatePortfolio(updatedPortfolio);
        redirectToNewAssessment(newAssessment.id);
    };

    const createTCOAssessmentAndRefresh = async (portfolioId: string, portfolioName: string) => {
        const newAssessment = await createTCOAssessment(portfolioId, portfolioName, tcoService);
        await saveNewAssessment(newAssessment);
        refreshList();
    };

    const onCreateButtonClick = async () => {
        try {
            updateTcoLoading(true);
            await createTCOAssessmentAndRefresh(portfolioId, portfolio.name);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, customizeErrorMessage((err as Error).message), constants.ERROR_CREATING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
        }
    };

    const onCalculateButtonClick = async () => {
        try {
            updateTcoLoading(true);
            if (assessmentToUpdate) {
                // we should double check that this assessment is a valid assessment(not the default generated with id 'New Assessment').
                if (assessmentToUpdate.id === defaultTcoAssessmentId) {
                    await createTCOAssessmentAndRefresh(portfolioId, portfolio.name);
                } else {
                    const updatedAssessment = await updateTCOAssessment(portfolioId, assessmentToUpdate, tcoService);
                    updateAssessment(updatedAssessment);
                }
            }
        } catch (err) {
            const message: string = (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message;
            if (isMounted()) {
                flash.alert(
                    FlashType.error,
                    customizeErrorMessage(message), 
                    constants.ERROR_UPDATING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
        }
    };

    const openAssumptionsModal = (): void => {
        setTcoAssumptionModalVisible(true);
    };

    const onAssumptionsModalSave = async (assumptions: AssessmentConfiguration, assumptionName?: string) => {
        try {
            updateTcoLoading(true);
            if (!tcoExists) { // if tco does not exist, create new assessment
                const assessmentToCreateWithMetadata: Partial<TcoAssessment> = Object.assign(
                    {configuration: assumptions},
                    {assumptionName},
                );
                const newAssessment = await createTCOAssessment(portfolioId, portfolio.name, tcoService, assessmentToCreateWithMetadata);
                updateAssessment(newAssessment);
                // If assessment is new, save to current portfolio
                await saveNewAssessment(newAssessment);
                //refresh recent portfolios list
                refreshList();
            } else if (assessmentToUpdate) {
                const assessmentToUpdateWithMetadata: TcoAssessment = Object.assign(
                    {...assessmentToUpdate},
                    {configuration: assumptions},
                    {assumptionName},
                );

                const updatedAssessment = await updateTCOAssessment(portfolioId, assessmentToUpdateWithMetadata, tcoService);
                updateAssessment(updatedAssessment);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(
                    FlashType.error, 
                    (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, 
                    constants.ERROR_SAVING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
        }
    };

    const onAssessmentAction = (event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        const action = event.detail.id;
        if (action === 'compare') {
            setAssessmentModalAction(FormAction.COMPARE);
            setAssessmentModalVisible(true);
        }
        else if (action === 'create') {
            setAssessmentModalAction(FormAction.ADD);
            setAssessmentModalVisible(true);
        } else if (action === 'edit') {
            setAssessmentModalAction(FormAction.EDIT);
            setAssessmentModalVisible(true);
            if (assessment) {
                const assessmentName = assessmentMetadatas.find(metadata => assessment.id === metadata.id)?.name ?? constants.DEFAULT_ASSESSMENT_NAME;
                setAssessmentToEdit({id: assessment.id, name: assessmentName, overrideAssumptions: false, copyAssessmentId: {}});
            }
        } else if (action === 'delete') {
            setAssessmentModalAction(FormAction.DELETE);
            setAssessmentDeleteModalVisible(true);
        }
    };

    const onNewAssessmentCreate = async (createAssessment: CreateNewAssessment) => {
        try {
            updateTcoLoading(true);
            // not using constant since it could change with language update
            const newAssessment = createAssessment.useDefault === 'true' ?
                await createTCOAssessment(portfolioId, portfolio.name, tcoService) :
                await createTCOAssessment(portfolioId, portfolio.name, tcoService, undefined, createAssessment.copyAssessmentId);
            await updateAssessmentName(newAssessment.id, createAssessment.name, metadataService);
            await updatePortfolioWithTcoAssessment(portfolio, newAssessment.id, portfolioService);
            refreshList();
            redirectToNewAssessment(newAssessment.id);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_CREATING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
        }
    };

    const onAssessmentEdit = async (updatedAssessment: EditAssessment) => {
        try {
            updateTcoLoading(true);
            await updateAssessmentName(tcoAssessmentId, updatedAssessment.name, metadataService);
            if (assessmentToUpdate && updatedAssessment.overrideAssumptions) {
                const updatedAssessmentToUpdate = await updateTCOAssessment(
                    portfolioId,
                    assessmentToUpdate,
                    tcoService, 
                    updatedAssessment.copyAssessmentId.value,
                );
                updateAssessment(updatedAssessmentToUpdate);
                history.go(0);
            } else {
                await loadMetadata();
            }
            refreshList();
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                updateTcoLoading(false);
            }
        }
    };

    const onAssessmentDelete = async () => {
        let redirectAssessmentId = '';
        try {
            updateTcoLoading(true);
            await tcoService.delete(tcoAssessmentId);
            const portfolioToUpdate = await deleteAssessmentFromPortfolio(portfolio, tcoAssessmentId, portfolioService, adjustmentsService);
            redirectAssessmentId = portfolioToUpdate.defaultTcoAssessmentId ?? 'default';
            updatePortfolio(portfolioToUpdate);
            refreshList();
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_DELETING_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setAssessmentDeleteModalVisible(false);
                updateTcoLoading(false);
                redirectToNewAssessment(redirectAssessmentId);
            }
        }
    };

    const createButton = (
        <Button loading={tcoLoading} onClick={onCreateButtonClick} id='btn-tco-create' variant='primary'>
            {constants.CALCULATE_BUTTON_TEXT}
        </Button>
    );

    const calculateButton = (
        <Button loading={tcoLoading} onClick={onCalculateButtonClick} id='btn-tco-calculate' variant='primary'>{constants.CALCULATE_BUTTON_TEXT}</Button>
    );

    const modifyButton = (
        <Button data-testid='btn-modify' disabled={tcoLoading} onClick={openAssumptionsModal}>{constants.MODIFY_ASSUMPTIONS}</Button>
    );

    const tcoDownloadClick = async () => {
        setDownloadLoading(true);
        try {
            const timezoneOffset = new Date().getTimezoneOffset();
            const assessmentMetadata = assessmentMetadatas.find(metadata => metadata.id === tcoAssessmentId);
            const params: DownloadAssessmentParams = {
                timezoneOffset,
                assessmentName: assessmentMetadata?.name ?? '',
                portfolioName: portfolio.name,
                accountName: portfolio.accountName ?? '',
            };
            const { url, awsDetailsRemoved }: DownloadAssessmentResponse = await tcoService.download(tcoAssessmentId, params);
            const win = window.open(url);
            if (awsDetailsRemoved && isMounted()) {
                flash.alert(FlashType.warning, constants.WARNING_AWS_DETAILS_EXCLUDED_CONTENT, constants.WARNING_AWS_DETAILS_EXCLUDED_HEADER);
            }
            if (!win && isMounted()) {
                flash.alert(FlashType.warning, constants.ERROR_OPENING_FILE_URL_CONTENT, constants.ERROR_OPENING_FILE_URL_HEADER);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message , constants.ERROR_DOWNLOADING_TCO_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setDownloadLoading(false);
            }
        }

    };

    const content = tcoExists && tcoSummary && assessment ?
        (
            <Box padding={{bottom: 'l'}}>
                <SpaceBetween size='l'>
                    <SavingsSummary
                        awsRightSizePolicyName={awsRightSizePolicyIdToString(assessment.configuration.awsEC2Pricing?.awsRightSizePolicy ?? '')}
                        duration={assessment.configuration.tcoAssumptions.durationYears}
                        region={assessment.configuration.tcoAssumptions.awsRegion}
                        downloadFn={tcoDownloadClick}
                        downloadLoading={downloadLoading}
                    />
                </SpaceBetween>
            </Box>
        ) :
        (
            <Container>
                <Box textAlign='center' className='landing-container'>
                    <h1>{constants.TOTAL_COST_OF_OWNERSHIP}</h1>
                    <p>{constants.TCO_DESCRIPTION}</p>
                    <p>{constants.TCO_DESCRIPTION_2}</p>
                    <Box display='inline-block' padding={{top: 'xl'}} textAlign='center'>
                        <Box display='inline-block' padding={{horizontal: 's'}}>
                            {createButton}
                        </Box>
                        <Box display='inline-block' padding={{horizontal: 's'}}>
                            {modifyButton}
                        </Box>
                    </Box>
                </Box>
            </Container> 
        );
    
    const outOfDateBanner = outOfDatePricing && (
        <Alert header={constants.OUT_OF_DATE_PRICING_HEADER}>
            <Box>{constants.OUT_OF_DATE_PRICING_P1}</Box>
        </Alert>
    );

    const assessmentDropdownItems: ButtonDropdownProps.Item[] = assessmentMetadatas.map(assessmentMetadata => ({
        id: assessmentMetadata.id,
        text: assessmentMetadata.name,
    }));

    const activeAssessment = assessmentMetadatas.find(assessmentMetadata => assessmentMetadata.id === tcoAssessmentId)?.name ?? constants.DEFAULT;

    const onAssessmentChange = ({ detail }: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        history.push(location.pathname.replace(tcoAssessmentId, detail.id));
    };

    return visible ? (
        <Box>
            <SpaceBetween size='l'>
                {outOfDateBanner}
                <div className='tco-action-bar'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        {tcoExists ? calculateButton : createButton}
                        {modifyButton}
                        <ButtonDropdown
                            data-testid='action-dropdown'
                            disabled={tcoLoading || !tcoExists}
                            items={assessmentActions}
                            onItemClick={onAssessmentAction}
                        >
                            {constants.ACTION_BUTTON_TEXT}
                        </ButtonDropdown>
                    </SpaceBetween>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Box float='right'>
                            <ButtonDropdown
                                data-testid='assessment-dropdown'
                                disabled={!(assessmentMetadatas?.length > 1) || tcoLoading}
                                items={assessmentDropdownItems}
                                onItemClick={onAssessmentChange}
                            >
                                {activeAssessment}
                            </ButtonDropdown>
                        </Box>
                    </SpaceBetween>
                </div>
                {!hideSavingsSummary && assessment ? content : null}
            </SpaceBetween>
            {tcoAssumptionModalVisible && (
                <TcoAssumptionsModal 
                    visible={tcoAssumptionModalVisible}
                    setVisible={setTcoAssumptionModalVisible}
                    onSave={onAssumptionsModalSave}
                />
            )}
            <AssessmentControlsModal
                visible={assessmentModalVisible}
                setVisible={setAssessmentModalVisible}
                onCreate={onNewAssessmentCreate}
                assessment={assessmentToEdit}
                formAction={assessmentModalAction}
                onEdit={onAssessmentEdit}
            />
            {assessmentDeleteModalVisible ? (
                <ConfirmationModal
                    visible={assessmentDeleteModalVisible}
                    header={constants.DELETE_ASSESSMENT_MODAL_TITLE}
                    friction={constants.DELETE_FRICTION}
                    onConfirm={onAssessmentDelete}
                    onDismiss={() => setAssessmentDeleteModalVisible(false)}
                />
            ) : null}
        </Box>
    ) : null;
};

export default withFlashContext(TcoActionPanel);
